import React, { useState, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../config/api'
import QuestionBuilder, { type QuestionData } from '../components/QuestionBuilder'
import { Button, Card, CardHeader, Input, Textarea, Select, Alert, StatusBadge, PageLayout, PageHeader } from '../components/ui'

const SURVEY_TYPES = [
  'Impact Assessment',
  'Needs Assessment',
  'Program Feedback',
  'Pre/Post Evaluation',
  'Custom',
]

interface SurveyQuestion {
  id: string
  text: string
  type: 'BOOLEAN' | 'TEXT' | 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'RATING' | 'COUNTRY'
  required: boolean
  order: number
  options: string[]
  scoring_config?: any
}

interface Survey {
  id: string
  title: string
  description: string
  organization_id: string
  status: string
  settings: Record<string, any>
  min_days_between_attempts: number
  auto_remind: boolean
  questions: SurveyQuestion[]
  recurrence_enabled?: boolean
  recurrence_interval_days?: number
  reminder_intervals?: number[]
}

const EditSurveyPage: React.FC = () => {
  const { surveyId = '' } = useParams<{ surveyId: string }>()
  const [searchParams] = useSearchParams()
  const orgId = searchParams.get('org')
  useAuth()
  const intl = useIntl()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [surveyType, setSurveyType] = useState('Impact Assessment')
  const [minDaysBetweenAttempts, setMinDaysBetweenAttempts] = useState(30)
  const [autoRemind, setAutoRemind] = useState(true)
  const [questions, setQuestions] = useState<QuestionData[]>([])
  const [surveyStatus, setSurveyStatus] = useState('DRAFT')
  const [originalQuestionIds, setOriginalQuestionIds] = useState<string[]>([])
  const [recurrenceEnabled, setRecurrenceEnabled] = useState(false)
  const [recurrenceIntervalDays, setRecurrenceIntervalDays] = useState(30)
  const [reminderIntervals, setReminderIntervals] = useState('0, 3, 7')

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const response = await fetch(API_ENDPOINTS.surveys.byId(surveyId), {
          headers: { 'Authorization': token ? `Bearer ${token}` : '' }
        })
        if (!response.ok) { setError(intl.formatMessage({ id: 'editSurvey.error.notFound' })); return }
        const data: Survey = await response.json()

        setTitle(data.title)
        setDescription(data.description || '')
        setSurveyType(data.settings?.survey_type || 'Impact Assessment')
        setMinDaysBetweenAttempts(data.min_days_between_attempts ?? 30)
        setAutoRemind(data.auto_remind ?? true)
        setSurveyStatus(data.status)
        setOriginalQuestionIds((data.questions || []).map(q => q.id))
        setRecurrenceEnabled(data.recurrence_enabled || false)
        setRecurrenceIntervalDays(data.recurrence_interval_days || 30)
        setReminderIntervals((data.reminder_intervals || [0, 3, 7]).join(', '))

        const loadedQuestions: QuestionData[] = (data.questions || []).map(q => ({
          text: q.text,
          type: q.type,
          required: q.required,
          order: q.order,
          options: q.options || [],
          scoring_config: q.scoring_config || null,
        }))
        setQuestions(loadedQuestions)
      } catch (err) {
        console.error('Error loading survey:', err)
        setError(intl.formatMessage({ id: 'editSurvey.error.loadFailed' }))
      } finally {
        setLoading(false)
      }
    }
    fetchSurvey()
  }, [surveyId])

  const handleSave = async () => {
    if (!title.trim()) { setError(intl.formatMessage({ id: 'editSurvey.error.titleRequired' })); return }

    setSaving(true)
    setError('')

    try {
      const token = localStorage.getItem('access_token')
      const headers = { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' }

      // Update survey details
      const updateResponse = await fetch(API_ENDPOINTS.surveys.byId(surveyId), {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          title,
          description,
          settings: { survey_type: surveyType },
          min_days_between_attempts: minDaysBetweenAttempts,
          auto_remind: autoRemind,
          recurrence_enabled: recurrenceEnabled,
          recurrence_interval_days: recurrenceEnabled ? recurrenceIntervalDays : null,
          reminder_intervals: reminderIntervals.split(',').map((s: string) => parseInt(s.trim(), 10)).filter((n: number) => !isNaN(n)),
        })
      })

      if (!updateResponse.ok) throw new Error('Failed to update survey')

      // Delete old questions and re-create them
      for (const qId of originalQuestionIds) {
        await fetch(`${API_ENDPOINTS.surveys.byId(surveyId)}/questions/${qId}`, {
          method: 'DELETE',
          headers,
        }).catch(() => {})
      }

      // Create new questions
      for (let i = 0; i < questions.length; i++) {
        const q = questions[i]
        await fetch(API_ENDPOINTS.surveys.questions(surveyId), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            text: q.text,
            type: q.type,
            required: q.required,
            order: i,
            options: q.options,
            scoring_config: q.scoring_config && q.scoring_config.type ? q.scoring_config : null,
          })
        })
      }

      navigate(`/surveys/${surveyId}/share`)
    } catch (err) {
      console.error('Error saving survey:', err)
      setError(intl.formatMessage({ id: 'editSurvey.error.saveFailed' }))
    } finally {
      setSaving(false)
    }
  }

  const surveyTypeLabels: Record<string, string> = {
    'Impact Assessment': intl.formatMessage({ id: 'editSurvey.surveyType.impactAssessment' }),
    'Needs Assessment': intl.formatMessage({ id: 'editSurvey.surveyType.needsAssessment' }),
    'Program Feedback': intl.formatMessage({ id: 'editSurvey.surveyType.programFeedback' }),
    'Pre/Post Evaluation': intl.formatMessage({ id: 'editSurvey.surveyType.prePostEvaluation' }),
    'Custom': intl.formatMessage({ id: 'editSurvey.surveyType.custom' }),
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground-muted"><FormattedMessage id="editSurvey.loading" /></div>
      </div>
    )
  }

  return (
    <PageLayout maxWidth="md">
      <PageHeader
        title={intl.formatMessage({ id: 'editSurvey.pageTitle' })}
        backTo={orgId ? `/organizations/${orgId}?tab=surveys` : `/surveys/${surveyId}/share`}
        backLabel={orgId ? intl.formatMessage({ id: 'editSurvey.backToOrganization' }) : intl.formatMessage({ id: 'editSurvey.backToSurvey' })}
        actions={<StatusBadge status={surveyStatus} />}
      />

      {error && (
        <Alert variant="error" className="mb-6">{error}</Alert>
      )}

      {/* Survey Details */}
      <Card className="mb-6">
        <CardHeader title={intl.formatMessage({ id: 'editSurvey.section.details' })} />
        <div className="space-y-4">
          <Input
            id="title"
            label={intl.formatMessage({ id: 'editSurvey.field.title' })}
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Textarea
            id="description"
            label={intl.formatMessage({ id: 'editSurvey.field.description' })}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Select
            id="surveyType"
            label={intl.formatMessage({ id: 'editSurvey.field.surveyType' })}
            value={surveyType}
            onChange={(e) => setSurveyType(e.target.value)}
            options={SURVEY_TYPES.map((t) => ({ value: t, label: surveyTypeLabels[t] || t }))}
          />
        </div>
      </Card>

      {/* Settings */}
      <Card className="mb-6">
        <CardHeader title={intl.formatMessage({ id: 'editSurvey.section.settings' })} />
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-foreground"><FormattedMessage id="editSurvey.daysBetweenRetakes" /></label>
            <input
              type="number"
              value={minDaysBetweenAttempts}
              onChange={(e) => setMinDaysBetweenAttempts(Math.max(0, parseInt(e.target.value) || 0))}
              min={0}
              max={365}
              className="w-20 px-2 py-1 rounded border border-border text-foreground bg-surface sm:text-sm"
            />
            <span className="text-xs text-foreground-muted"><FormattedMessage id="editSurvey.noLimitHint" /></span>
          </div>
          <label className="flex items-center">
            <input type="checkbox" checked={autoRemind}
              onChange={(e) => setAutoRemind(e.target.checked)}
              className="h-4 w-4 text-primary border-border rounded mr-2" />
            <span className="text-sm text-foreground"><FormattedMessage id="editSurvey.autoRemind" /></span>
          </label>
        </div>
      </Card>

      {/* Questions */}
      <Card className="mb-6">
        <CardHeader title={intl.formatMessage({ id: 'editSurvey.section.questions' }, { count: questions.length })} />
        <QuestionBuilder questions={questions} onChange={setQuestions} />
      </Card>

      {/* Recurrence & Reminders */}
      <Card className="mb-6">
        <CardHeader title={intl.formatMessage({ id: 'editSurvey.section.recurrence' })} subtitle={intl.formatMessage({ id: 'editSurvey.section.recurrenceSubtitle' })} />
        <div className="space-y-4">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={recurrenceEnabled}
              onChange={(e) => setRecurrenceEnabled(e.target.checked)}
              className="rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm font-medium text-foreground"><FormattedMessage id="editSurvey.enableRecurrence" /></span>
          </label>

          {recurrenceEnabled && (
            <div className="space-y-4 pl-7">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1"><FormattedMessage id="editSurvey.repeatInterval" /></label>
                <select
                  value={recurrenceIntervalDays}
                  onChange={(e) => setRecurrenceIntervalDays(Number(e.target.value))}
                  className="block w-full px-3 py-2 rounded-lg border border-border text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm"
                >
                  <option value={7}>{intl.formatMessage({ id: 'editSurvey.interval.weekly' })}</option>
                  <option value={14}>{intl.formatMessage({ id: 'editSurvey.interval.biweekly' })}</option>
                  <option value={30}>{intl.formatMessage({ id: 'editSurvey.interval.monthly' })}</option>
                  <option value={90}>{intl.formatMessage({ id: 'editSurvey.interval.quarterly' })}</option>
                  <option value={180}>{intl.formatMessage({ id: 'editSurvey.interval.semiAnnual' })}</option>
                  <option value={365}>{intl.formatMessage({ id: 'editSurvey.interval.annual' })}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  <FormattedMessage id="editSurvey.reminderSchedule" />
                </label>
                <input
                  type="text"
                  value={reminderIntervals}
                  onChange={(e) => setReminderIntervals(e.target.value)}
                  placeholder={intl.formatMessage({ id: 'editSurvey.reminderPlaceholder' })}
                  className="block w-full px-3 py-2 rounded-lg border border-border text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm"
                />
                <p className="text-xs text-foreground-muted mt-1">
                  <FormattedMessage id="editSurvey.reminderHint" />
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Actions */}
      <div className="flex justify-between">
        <Link to={`/surveys/${surveyId}/share`}>
          <Button variant="outline"><FormattedMessage id="editSurvey.cancel" /></Button>
        </Link>
        <Button variant="primary" onClick={handleSave} loading={saving}>
          {saving ? <FormattedMessage id="editSurvey.saving" /> : <FormattedMessage id="editSurvey.saveChanges" />}
        </Button>
      </div>
    </PageLayout>
  )
}

export default EditSurveyPage
